import { useMemo } from "react"
import { useCustomerData } from "context/customer-provider"
import type { AvailableCustomer } from "types/customer"
import { useAuth0 } from "@auth0/auth0-react"

export type Access = {
    // the signed-in account's role at the ACTIVE customer: "super-admin" for
    // RR staff, null when the list has no entry for it ("member" in the
    // degraded fallback list — so a viewer gets 403s there, not hidden controls)
    role: string | null
    // view only: may view, filter and export, but not change anything
    isViewOnly: boolean
    // the inverse, for the common `{canEdit && <Button …/>}` gate
    canEdit: boolean
    // may add / remove users and change roles here — rr-api's
    // callerMayManageMembers: RR staff, or this customer's row says admin
    // (the Auth0 admin role stands in only for the degraded fallback list)
    canManageUsers: boolean
}

// Dev-only preview of a viewer's UI for any account (RR staff are never
// viewers): load the app with ?viewOnly=1. Captured once per page load, like
// the ?hideWorkPackages=1 preview, so it sticks across SPA navigations. The
// server is not fooled — a super-admin's writes still go through.
const devPreviewViewOnly =
    import.meta.env.DEV && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("viewOnly") === "1"

// The rule, kept pure so it can be table-tested and stays identical to
// rr-api's view-only check (api/utils/view-only.js): super-admins are never
// viewers; otherwise the active customer's row role decides. Unsettled lists
// (the boot snapshot) are used as they are — the server refuses a stale
// viewer's writes regardless.
export const resolveAccess = (
    customerId: string,
    customers: AvailableCustomer[],
    {
        isSuperAdmin = false,
        isAuth0Admin = false,
        previewViewOnly = false,
    }: { isSuperAdmin?: boolean; isAuth0Admin?: boolean; previewViewOnly?: boolean } = {},
): Access => {
    if (previewViewOnly) return { role: "viewer", isViewOnly: true, canEdit: false, canManageUsers: false }
    if (isSuperAdmin) return { role: "super-admin", isViewOnly: false, canEdit: true, canManageUsers: true }
    const entry = customers.find((customer) => customer.customer_id === customerId)
    const role = entry?.role ?? null
    const isViewOnly = role === "viewer"
    const rowBacked = entry?.source === "home" || entry?.source === "membership"
    return { role, isViewOnly, canEdit: !isViewOnly, canManageUsers: rowBacked ? role === "admin" : isAuth0Admin }
}

// View-only access at the active customer. Every control that changes data
// (status, edits, drawing, packages, comments, …) is gated on canEdit; the
// API refuses a viewer's writes either way (403 view_only).
const useAccess = (): Access => {
    const { customerId, availableCustomers } = useCustomerData()
    // The roles claim itself (as useAuth0Metadata reads it) — not that hook,
    // which also loads the profile; this one renders in many small components.
    const { user } = useAuth0()
    const roles: string[] = user?.["https://routereports.com/roles"] ?? []
    const userIsSuperAdmin = roles.includes("super-admin")
    const userIsAdmin = roles.includes("admin")
    return useMemo(
        () =>
            // ?? []: test doubles of the customer context may omit the list
            resolveAccess(customerId, availableCustomers ?? [], {
                isSuperAdmin: !!userIsSuperAdmin,
                isAuth0Admin: !!userIsAdmin,
                previewViewOnly: devPreviewViewOnly,
            }),
        [availableCustomers, customerId, userIsAdmin, userIsSuperAdmin],
    )
}

export default useAccess
